import type { SquadArchetype } from '../simulation/entities/Squad.js';

export interface SquadDef {
  archetype: SquadArchetype;
  cost: number;
  buildTimeSec: number;
  maxHp: number;
  dps: number;
  range: number;
  vision: number;
  speed: number;
  members: number;
  // V0.3 combat: explicit per-shot damage and cooldown so DPS isn't a divisor.
  // Invariant: damagePerShot / attackCooldownSec ≈ dps. Tweak together.
  damagePerShot: number;
  attackCooldownSec: number;
  projectileSpeed: number;
}

// Tuning numbers from docs/02_GAME_DESIGN.md §2. V2 "Tempo" patch (2026-05-24):
// build times trimmed ~25% and move speeds bumped ~25% so the early game has more
// fights per minute. Combat math (dps / range / damagePerShot / attackCooldownSec)
// is unchanged — fights resolve at the same speed, but units reach contact sooner
// and queues turn over faster. CrazyGames analytics (avg playtime 3m09s vs 7m52s
// benchmark) showed players bouncing in the dead-time before first contact; the
// Tempo patch compresses that without making combat itself feel twitchy.
export const SQUAD_DEFS: Record<SquadArchetype, SquadDef> = {
  recon: {
    archetype: 'recon',
    cost: 60,
    buildTimeSec: 4.5,
    maxHp: 90,
    dps: 12,
    range: 250,
    vision: 14,
    speed: 140,
    members: 2,
    damagePerShot: 12,
    attackCooldownSec: 1.0,
    projectileSpeed: 720,
  },
  assault: {
    archetype: 'assault',
    cost: 120,
    buildTimeSec: 9,
    maxHp: 320,
    dps: 60,
    range: 280,
    vision: 8,
    speed: 115,
    members: 4,
    damagePerShot: 30,
    attackCooldownSec: 0.5,
    projectileSpeed: 640,
  },
  engineer: {
    archetype: 'engineer',
    cost: 100,
    buildTimeSec: 7.5,
    maxHp: 160,
    dps: 20,
    range: 220,
    vision: 7,
    speed: 105,
    members: 2,
    damagePerShot: 10,
    attackCooldownSec: 0.5,
    projectileSpeed: 660,
  },
  shield: {
    archetype: 'shield',
    cost: 140,
    buildTimeSec: 10.5,
    maxHp: 480,
    dps: 25,
    range: 220,
    vision: 6,
    speed: 90,
    members: 2,
    damagePerShot: 25,
    attackCooldownSec: 1.0,
    projectileSpeed: 600,
  },
};
